import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography } from "@mui/material";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import StatusChip from "../components/common/StatusChip";
import DateRangeFilter from "../components/common/DateRangeFilter";

import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AcademicYearSelectFilter from "../components/common/AcademicYearSelectFilter";
import ClassroomSelectFilter from "../components/common/ClassroomSelectFilter";
import StudentSearchSelect from "../components/common/StudentSearchSelect";
import AppSelectFilter from "../components/common/AppSelectFilter";

import TransitionWizardModal from "../components/transitionRecords/TransitionWizardModal";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getTransitionRecords,
  resetTransitionRecordStatus,
} from "../redux/actions/transitionRecordActions";

import { usePermissions } from "../hooks/usePermissions";
import useDebounce from "../hooks/useDebounce";
import { TransitionRecordResponseDto } from "../shared/dtos/transitionRecord/TransitionRecordResponseDto";
import { TransitionStatus } from "../shared/enums/TransitionStatus";
import { TRANSITION_STATUS_OPTIONS } from "../shared/data/enumOptions";
import { formatDateForDisplay } from "../helpers/dateHelpers";

const TransitionRecordsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    transitionRecords,
    pagination,
    loadingTransitionRecords,
    errorLoadingTransitionRecords,
    serverStatus,
  } = useAppSelector((state) => state.transitionRecord);

  // Filters State
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const [selectedFromYearId, setSelectedFromYearId] = useState<string>("");
  const [selectedToYearId, setSelectedToYearId] = useState<string>("");
  const [selectedFromClassroomId, setSelectedFromClassroomId] =
    useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 15 });
  const [isWizardOpen, setIsWizardOpen] = useState<boolean>(false);

  const canExecute = hasPermission("transition_record:create");

  const fetchHistory = useCallback((): void => {
    dispatch(
      getTransitionRecords({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
        studentId: selectedStudentId || undefined,
        fromAcademicYearId: selectedFromYearId || undefined,
        toAcademicYearId: selectedToYearId || undefined,
        fromClassroomId: selectedFromClassroomId || undefined,
        status: (selectedStatus as TransitionStatus) || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    );
  }, [
    dispatch,
    pageConfig,
    debouncedSearch,
    selectedBranchId,
    selectedStudentId,
    selectedFromYearId,
    selectedToYearId,
    selectedFromClassroomId,
    selectedStatus,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Reset page to 1 whenever any filter changes
  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [
    selectedBranchId,
    selectedStudentId,
    selectedFromYearId,
    selectedToYearId,
    selectedFromClassroomId,
    selectedStatus,
    startDate,
    endDate,
    debouncedSearch,
  ]);

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "execute" && status === 200) {
      setIsWizardOpen(false);
      fetchHistory();
    }

    dispatch(resetTransitionRecordStatus());
  }, [serverStatus, dispatch, fetchHistory]);

  const columns = useMemo<TableColumn<TransitionRecordResponseDto>[]>(
    () => [
      {
        id: "studentName",
        label: "Student Name",
        minWidth: 180,
        format: (_, row) => (
          <Typography
            component="span"
            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
          >
            {row.studentName}
          </Typography>
        ),
      },
      { id: "admissionNumber", label: "Adm No.", minWidth: 100 },
      { id: "fromGradeLevelName", label: "From Grade", minWidth: 120 },
      { id: "toGradeLevelName", label: "To Grade", minWidth: 120 },
      { id: "fromClassroomName", label: "From Stream", minWidth: 120 },
      {
        id: "status",
        label: "Status",
        minWidth: 120,
        format: (_, row) => <StatusChip status={row.status as any} />,
      },
      {
        id: "transitionDate",
        label: "Date",
        minWidth: 110,
        format: (_, row) => formatDateForDisplay(row.transitionDate),
      },
      { id: "processedByName", label: "Processed By", minWidth: 150 },
    ],
    [],
  );

  return (
    <PageContainer
      title="Student Transitions"
      subtitle="Track academic progression history and manage end-of-year promotions."
      actions={
        canExecute && (
          <AppButton
            variant="contained"
            startIcon={<PublishedWithChangesIcon />}
            onClick={() => setIsWizardOpen(true)}
          >
            Start Transition Wizard
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search by student name or admission no..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={setSelectedBranchId}
        />

        <StudentSearchSelect
          value={selectedStudentId}
          branchId={selectedBranchId}
          onChange={setSelectedStudentId}
        />

        <AcademicYearSelectFilter
          label="From Year"
          value={selectedFromYearId}
          onChange={setSelectedFromYearId}
        />

        <AcademicYearSelectFilter
          label="To Year"
          value={selectedToYearId}
          onChange={setSelectedToYearId}
        />

        <ClassroomSelectFilter
          label="From Stream"
          value={selectedFromClassroomId}
          branchId={selectedBranchId}
          onChange={setSelectedFromClassroomId}
        />

        <AppSelectFilter
          label="Status"
          value={selectedStatus}
          onChange={setSelectedStatus}
          options={TRANSITION_STATUS_OPTIONS}
          allLabel="All Statuses"
          minWidth={130}
        />

        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      <StandardTable<TransitionRecordResponseDto>
        columns={columns}
        data={transitionRecords}
        loading={loadingTransitionRecords}
        error={errorLoadingTransitionRecords}
        onRetry={fetchHistory}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((prev) => ({ ...prev, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((prev) => ({ ...prev, limit: l, page: 1 }))
        }
        emptyMessage="No transition records found"
      />

      {isWizardOpen && (
        <TransitionWizardModal
          open={isWizardOpen}
          onClose={() => setIsWizardOpen(false)}
        />
      )}
    </PageContainer>
  );
};

export default TransitionRecordsPage;
